import { BKJButton } from '@bookjane2/bookjane-design-library';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { BKJTableWrapper } from 'components/BKJTable/BKJTable.styled';
import { BoldText, Text } from 'modals/ShiftModal/src/views/AuditTrailView/AuditTrailView.styled';
import { Flex } from 'components/Flex';
import AuditTrailHeader from 'modals/ShiftModal/src/views/AuditTrailView/AuditTrailHeader';
import { AuditTrailTableView } from 'modals/ShiftModal/src/views/AuditTrailView/AuditTrailTableView';
import { FEATURE_SELECTORS_MAP } from 'guards/FeatureGuard.constants';
import { Fragment, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'hooks/useTranslation';
import { css } from 'styled-components';
import {
  AuditTrailDataProvider,
  useAuditTrailDataContext,
} from 'modals/ShiftModal/src/views/AuditTrailView/AuditTrailView.data.context';
import { GET_callOutListCSV } from 'modals/ShiftModal/src/views/AuditTrailView/AuditTrailView.constants';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { saveResponseAsCSV } from 'utils/saveResponseAsCSV';
import { CalloutList } from 'types/graphql';
import { DateTime } from 'luxon';

export function DetailViewFooter() {
  const { t } = useTranslation();
  const isReadOnly = useSelector(
    FEATURE_SELECTORS_MAP['FEATURE_TYPE_YMCA_READ_ONLY_SECONDARY_USER'],
  );

  const {
    data,
    estimatedTimeRemaining: { mins, hours, days },
  } = useAuditTrailDataContext();

  const callout_list = `${data?.node?.id}`;

  const exportShiftId = (data?.node as CalloutList)?.order?.orderSchedule?.id;

  const requestOptions = GET_callOutListCSV({
    callout_list,
    data: 'csv',
  });

  const { apiRequest } = useAPIActions();

  const exportCallOutListCSV = useCallback(() => {
    apiRequest(requestOptions, {
      onSuccess: (response) => {
        saveResponseAsCSV(
          response,
          `${t('shift_modal:AUDIT_TRAIL_SHIFT_ID')} #${exportShiftId} - ${t(
            'shift_modal:EXPORTED',
          )} ${DateTime.now().toFormat('MMM d yyyy')}.csv`,
        );
      },
    });
  }, [apiRequest, exportShiftId, requestOptions, t]);

  const {
    totalNotifiedAndSkipped = 0,
    totalJanes = 0,
    order: { biddable = false, isBlockShift = false } = {},
  } = !!data && !!data?.node ? (data.node as CalloutList) : {};

  const BiddableFooterView = useCallback(() => {
    return (
      <Fragment>
        <BoldText>
          {t(
            isBlockShift
              ? 'shift_modal:BIDDING_SHIFT_SENT_BLOCK'
              : 'shift_modal:BIDDING_SHIFT_SENT',
          )}{' '}
        </BoldText>
        <Text>
          {t('shift_modal:SENT_OUT_TO_TEAM_MEMBERS', {
            count: totalNotifiedAndSkipped,
            totalCount: totalJanes,
          })}
        </Text>
      </Fragment>
    );
  }, [isBlockShift, t, totalJanes, totalNotifiedAndSkipped]);

  const CalloutFooter = useCallback(() => {
    return (
      <Fragment>
        <Text>
          <BoldText>{t('shift_modal:CALL_OUT_PROGRESS')}: </BoldText>
          {totalNotifiedAndSkipped}/{totalJanes}
        </Text>
        <Text>
          <BoldText>{t('shift_modal:EST_TIME_REMAINING')}: </BoldText>
          {days > 0 && <span>{days}d&nbsp;</span>}
          {hours > 0 && <span>{hours}hr&nbsp;</span>}
          {mins > 0 && <span>{mins}m&nbsp;</span>}
          {days <= 0 && hours <= 0 && mins <= 0 && <span>-</span>}
        </Text>
      </Fragment>
    );
  }, [days, hours, mins, t, totalJanes, totalNotifiedAndSkipped]);

  if (isReadOnly || !data) return <Fragment />;

  return (
    <BKJSideModalFooter>
      <Flex justifyContent="space-between" width="100%">
        <Flex flexDirection="column" width="100%">
          {biddable ? <BiddableFooterView /> : <CalloutFooter />}
        </Flex>
        <Flex justifyContent="flex-end">
          <BKJButton
            width="180px"
            variant="PurpleOutlined"
            css={css`
              margin-right: 8px;
            `}
            onClick={exportCallOutListCSV}
          >
            {t('shift_modal:EXPORT_CSV')}
          </BKJButton>
        </Flex>
      </Flex>
    </BKJSideModalFooter>
  );
}

export const AuditTrailTabView = () => {
  return (
    <AuditTrailDataProvider>
      <BKJTableWrapper>
        <Flex flexDirection="column">
          <AuditTrailHeader />
          <AuditTrailTableView />
        </Flex>
      </BKJTableWrapper>
      <DetailViewFooter />
    </AuditTrailDataProvider>
  );
};
